import {
  Button,
  Input,
  Spinner,
  Text,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import {
  CheckmarkRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  PeopleRegular,
  SearchRegular,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";

import {
  TeamSummary,
  getTeamAvatarColor,
} from "../TeamSummary/TeamSummary";
import { useExistingTeamPickerStyles } from "./ExistingTeamPicker.styles";
import {
  IExistingTeamPickerProps,
  IMicrosoftTeam,
} from "./ExistingTeamPicker.types";

const MOCK_TEAMS: IMicrosoftTeam[] = [
  {
    id: "team-1",
    name: "MER-1042 Shared Services PMO",
    memberCount: 11,
    ownerName: "Priya Anand",
  },
  {
    id: "team-2",
    name: "Meridian Health — Account Team",
    memberCount: 38,
    ownerName: "Theo Ruiz",
  },
  {
    id: "team-3",
    name: "Meridian Health — Transformation",
    memberCount: 24,
    ownerName: "Devin Mara",
  },
  {
    id: "team-4",
    name: "Delivery Excellence",
    memberCount: 164,
    ownerName: "Jamie Lowe",
  },
  {
    id: "team-5",
    name: "Harborline Insurance — Claims",
    memberCount: 19,
    ownerName: "Priya Anand",
    connectedWorkspaceName: "Claims Operating Model",
  },
  {
    id: "team-6",
    name: "Healthcare Practice",
    memberCount: 212,
    ownerName: "Rena Kohl",
  },
  {
    id: "team-7",
    name: "Meridian Health — Clinical Operations",
    memberCount: 32,
    ownerName: "Alex Morgan",
  },
  {
    id: "team-8",
    name: "Digital Transformation Office",
    memberCount: 47,
    ownerName: "Sophie Bennett",
  },
  {
    id: "team-9",
    name: "Harborline Insurance — Underwriting",
    memberCount: 26,
    ownerName: "Marcus Reed",
    connectedWorkspaceName: "Underwriting Modernization",
  },
  {
    id: "team-10",
    name: "Enterprise Architecture",
    memberCount: 18,
    ownerName: "Olivia Chen",
  },
  {
    id: "team-11",
    name: "Meridian Health — Data & Analytics",
    memberCount: 56,
    ownerName: "Daniel Foster",
  },
  {
    id: "team-12",
    name: "Project Management Office",
    memberCount: 73,
    ownerName: "Emma Wilson",
    connectedWorkspaceName: "PMO Central",
  },
  {
    id: "team-13",
    name: "Customer Experience Strategy",
    memberCount: 29,
    ownerName: "Noah Parker",
  },
];

const getMicrosoftTeams = (): Promise<IMicrosoftTeam[]> =>
  Promise.resolve(MOCK_TEAMS);

const TEAMS_PER_PAGE = 5;

export const ExistingTeamPicker = ({
  workspaceCode,
  clientName,
  selectedTeamId,
  onSelectedTeamIdChange,
}: IExistingTeamPickerProps): JSX.Element => {
  const styles = useExistingTeamPickerStyles();
  const [searchText, setSearchText] = useState<string>("");
  const [teams, setTeams] = useState<IMicrosoftTeam[]>([]);
  const [isTeamsLoading, setIsTeamsLoading] = useState<boolean>(true);
  const [isTeamsLoadError, setIsTeamsLoadError] = useState<boolean>(false);
  const [teamsLoadAttempt, setTeamsLoadAttempt] = useState<number>(0);
  const [pageIndex, setPageIndex] = useState<number>(0);

  useEffect((): (() => void) => {
    let isCancelled = false;
    setIsTeamsLoading(true);
    setIsTeamsLoadError(false);
    getMicrosoftTeams()
      .then((loadedTeams) => {
        if (!isCancelled) setTeams(loadedTeams);
      })
      .catch(() => {
        if (!isCancelled) setIsTeamsLoadError(true);
      })
      .finally(() => {
        if (!isCancelled) setIsTeamsLoading(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [teamsLoadAttempt]);

  const sortedTeams = useMemo(() => {
    return teams
      .map((team) => ({
        team
      }))
      .sort(
        (a, b) =>
          a.team.name.localeCompare(b.team.name),
      );
  }, [teams, workspaceCode, clientName]);

  const trimmedSearchText = searchText.trim();
  const filteredTeams = useMemo(() => {
    const query = trimmedSearchText.toLowerCase();
    return sortedTeams.filter(({ team }) =>
      team.name.toLowerCase().includes(query),
    );
  }, [sortedTeams, trimmedSearchText]);

  const totalPages = Math.max(1, Math.ceil(filteredTeams.length / TEAMS_PER_PAGE));
  const currentPageIndex = Math.min(pageIndex, totalPages - 1);
  const pagedTeams = filteredTeams.slice(
    currentPageIndex * TEAMS_PER_PAGE,
    (currentPageIndex + 1) * TEAMS_PER_PAGE,
  );

  return (
    <div className={styles.teamPicker}>
      <div className={styles.teamListCard}>
        <div className={styles.searchBar}>
          <Input
            className={styles.searchInput}
            appearance="filled-lighter"
            contentBefore={<SearchRegular aria-hidden="true" />}
            placeholder="Search the Teams you can access"
            aria-label="Search Teams"
            value={searchText}
            onChange={(_, data) => {
              setSearchText(data.value);
              setPageIndex(0);
            }}
          />
        </div>
        <div role="radiogroup" aria-label="Microsoft Teams">
          {isTeamsLoading && (
            <div className={styles.emptyState}>
              <Spinner size="small" label="Loading your Teams..." />
            </div>
          )}
          {!isTeamsLoading && isTeamsLoadError && (
            <div className={styles.emptyState} role="alert">
              <Text block>We couldn&apos;t load your Teams.</Text>
              <Button
                appearance="secondary"
                onClick={() => setTeamsLoadAttempt((attempt) => attempt + 1)}
              >
                Retry
              </Button>
            </div>
          )}
          {!isTeamsLoading &&
            !isTeamsLoadError &&
            filteredTeams.length === 0 && (
              <div className={styles.emptyState}>
                {trimmedSearchText
                  ? `No Teams match "${trimmedSearchText}". Try a different name, or create a new Team.`
                  : "You are not a member of any Microsoft Team. Create a new Team instead."}
              </div>
            )}
          {pagedTeams.map(({ team }) => {
            const isDisabled = Boolean(team.connectedWorkspaceName);
            const isSelected = selectedTeamId === team.id;
            const avatarColor = getTeamAvatarColor(
              sortedTeams.findIndex((entry) => entry.team.id === team.id),
            );
            return (
              <button
                key={team.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                className={mergeClasses(
                  styles.teamRow,
                  isSelected && styles.teamRowSelected,
                  isDisabled && styles.teamRowDisabled,
                )}
                onClick={() => {
                  if (!isDisabled) onSelectedTeamIdChange(team.id);
                }}
              >
                <span
                  className={mergeClasses(
                    styles.radioIndicator,
                    isSelected && styles.radioIndicatorSelected,
                  )}
                >
                  {isSelected && <CheckmarkRegular aria-hidden="true" />}
                </span>
                <TeamSummary team={team} avatarColor={avatarColor} />
                {isDisabled && (
                  <Tooltip
                    content={`Already connected to ${team.connectedWorkspaceName}`}
                    relationship="description"
                    withArrow
                  >
                    <span className={styles.connectedNote}>
                      Already connected to {team.connectedWorkspaceName}
                    </span>
                  </Tooltip>
                )}
              </button>
            );
          })}
        </div>
        {!isTeamsLoading && !isTeamsLoadError && totalPages > 1 && (
          <div className={styles.pagination}>
            <Text className={styles.paginationLabel} aria-live="polite">
              Page {currentPageIndex + 1} of {totalPages}
            </Text>
            <div className={styles.paginationActions}>
              <Button
                appearance="subtle"
                size="small"
                icon={<ChevronLeftRegular aria-hidden="true" />}
                disabled={currentPageIndex === 0}
                onClick={() => setPageIndex(currentPageIndex - 1)}
                aria-label="Previous page"
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<ChevronRightRegular aria-hidden="true" />}
                disabled={currentPageIndex === totalPages - 1}
                onClick={() => setPageIndex(currentPageIndex + 1)}
                aria-label="Next page"
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.syncNote}>
        <PeopleRegular
          className={styles.syncNoteIcon}
          aria-hidden="true"
        />
        <Text className={styles.noteText}>
          All members of the selected Microsoft Team will be synced and
          added as team members of this Project IQ Workspace.
        </Text>
      </div>
    </div>
  );
};
